import { jsxs, jsx } from 'react/jsx-runtime';
import { createRef, useMemo, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import { useCombobox } from 'downshift';
import { TextField, IconName } from '@sbt-web/ui';
import { formatDistance } from '@sbt-web/utils';
import itemClasses from '../common/Item.module.scss.js';
import RecentSearches from '../common/RecentSearches.js';
import SuggestionResult from '../common/SuggestionResult.js';
import { buildSearchCancelEvent, buildSearchSubmitEvent } from '../common/tracking.js';
import { isGeogosAutocompleteOption } from '../common/types.js';
import { mapper } from '../useGeogos.js';
import classes from './index.module.scss.js';

const textFieldRef = createRef();
function DesktopGeoSearch({ value, options, labelId, name, onSelect, pulse, updateItems, storeRecentSearch, assetsBase, }) {
    const initializedInputValue = useMemo(() => {
        if (value.responseFrom === 'geogos') {
            return mapper.translateGeoEntry(value.entry).inputLabel;
        }
        else {
            return value.entry.friendlyName;
        }
    }, [value.entry, value.responseFrom]);
    const { getComboboxProps, getInputProps, getItemProps, getMenuProps, highlightedIndex, inputValue, isOpen, openMenu, reset, selectedItem, setInputValue, } = useCombobox({
        id: 'desktop-geo-search',
        items: options.items,
        labelId,
        initialInputValue: initializedInputValue,
        itemToString: (item) => item?.inputLabel ?? '',
        onInputValueChange: (changes) => {
            updateItems('geogos', changes.inputValue);
        },
        onSelectedItemChange: ({ selectedItem }) => {
            if (selectedItem == null) {
                return;
            }
            textFieldRef.current?.blur();
            if (selectedItem.value.responseFrom === 'geogos') {
                pulse?.queueEvent(buildSearchSubmitEvent(selectedItem.value, selectedItem.inputLabel, highlightedIndex + 1, selectedItem.isRecentSearch));
                onSelect(selectedItem.value);
            }
            if (selectedItem != null && isGeogosAutocompleteOption(selectedItem)) {
                storeRecentSearch?.(selectedItem);
            }
        },
        onStateChange: (changes) => {
            if (changes.type === useCombobox.stateChangeTypes.InputKeyDownEscape) {
                pulse?.queueEvent(buildSearchCancelEvent());
            }
            else if (changes.type === useCombobox.stateChangeTypes.ItemClick &&
                changes.selectedItem == null) {
                // Only intervene if the selected item hasn't changed.
                // Otherwise let Downshift do the work.
                textFieldRef.current?.blur();
                pulse?.queueEvent(buildSearchSubmitEvent(selectedItem?.value, selectedItem?.inputLabel, highlightedIndex + 1, selectedItem?.isRecentSearch));
            }
        },
    });
    useEffect(() => {
        if (value.responseFrom === 'geogos') {
            const nameToDisplay = mapper.translateGeoEntry(value.entry, value.searchNearRegions).inputLabel;
            setInputValue(nameToDisplay);
        }
        else {
            const nameToDisplay = value.entry.friendlyName;
            reset();
            setInputValue(`Entro ${formatDistance(value.radius)} da ${nameToDisplay}`);
        }
    }, [value, reset, setInputValue]);
    const clearInput = useCallback(() => {
        setInputValue('');
        textFieldRef.current?.focus();
    }, [setInputValue]);
    return (jsxs("div", { className: classes.container, children: [jsx("div", { ...getComboboxProps(), children: jsx(TextField, { size: "large", icon: IconName.Pin, withClearIcon: inputValue?.length > 0, onInputChange: (e) => setInputValue(e.target.value), onCleared: clearInput, disableNativeAC: true, ...getInputProps({
                        ref: textFieldRef,
                        type: 'search',
                        placeholder: 'Comune, Provincia o Regione',
                        name,
                        value: inputValue,
                        onFocus: () => {
                            setInputValue('');
                            openMenu();
                        },
                    }) }) }), jsx("div", { className: classNames(classes['dropdown-wrapper'], {
                    [classes.open]: isOpen,
                }), children: jsxs("ul", { className: classNames(classes.dropdown, {
                        [classes.results]: options.kind === 'search',
                        [classes['dropdown--is-open']]: isOpen,
                    }), ...getMenuProps(), children: [isOpen &&
                            options.kind === 'search' &&
                            options.items.map((item, index) => (jsx("li", { className: classNames(itemClasses.item, itemClasses['size-small'], {
                                    [itemClasses.selected]: highlightedIndex === index,
                                }), ...getItemProps({ item, index }), children: jsx(SuggestionResult, { geoOptionsLabels: item.optionLabels, query: inputValue }) }, `${item.inputLabel}${index}`))), isOpen &&
                            options.kind === 'recent' &&
                            (selectedItem == null ||
                                isGeogosAutocompleteOption(selectedItem)) && (jsx(RecentSearches, { variant: "desktop", getItemProps: getItemProps, highlightedIndex: highlightedIndex, searches: options.items, selectedItem: selectedItem, assetsBase: assetsBase })), isOpen && options.kind === 'new' && jsx(NewSearch, {})] }) })] }));
}
function NewSearch() {
    return (jsxs("div", { className: classes.newsearch, children: [jsx("p", { className: "body-text semibold small", children: "Inserisci il luogo" }), jsx("p", { className: `body-text small ${classes.helptext}`, children: "Scrivi qui il comune, la provincia o la regione della tua ricerca" })] }));
}

export { DesktopGeoSearch as default };
//# sourceMappingURL=index.js.map
